import { useQuery } from '@tanstack/react-query';

import { listUserInvitesPaginated } from '@/features/users/services/userInvites';
import type {
  InvitationsListResponse,
  ListInvitationsParams,
} from '@/features/users/types/users.types';

export const USER_INVITES_QUERY_KEY = ['user-invites'] as const;

/**
 * useUserInvites
 *
 * Fetches the invitations list for the current user's scope
 * with server-side pagination.
 *
 * @param params - Pagination parameters (offset, limit)
 * @returns TanStack Query result with paginated invitations
 *
 * @example
 * ```tsx
 * const { data, isLoading, error } = useUserInvites({ offset: 0, limit: 10 });
 *
 * if (isLoading) return <Spinner />;
 *
 * return <Table dataSource={data.content} />;
 * ```
 */
export function useUserInvites(params: ListInvitationsParams = {}) {
  return useQuery<InvitationsListResponse, Error>({
    queryKey: [...USER_INVITES_QUERY_KEY, params],
    queryFn: () => listUserInvitesPaginated(params),
    retry: (failureCount, error) => {
      // Never retry 401 – the user simply lacks permission for this endpoint
      if (
        'response' in error &&
        (error as { response?: { status?: number } }).response?.status === 401
      ) {
        return false;
      }
      return failureCount < 1;
    },
    refetchOnWindowFocus: false,
  });
}
